import { Trans, useTranslation } from 'react-i18next';
import { Popover } from '@c2fo/liquidity';
import { ClockIcon, InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import beelineSvg from '@/enterprise/assets/beeline.svg?url';
import useGroupByBuyer from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useCountdown from '@/utils/useCountdown';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import Skeleton from '../Skeleton';

const HeroBannerLayout = ({
  bannerComponent,
  countdownComponent,
}: {
  bannerComponent: React.ReactNode;
  countdownComponent?: React.ReactNode;
}) => {
  return (
    <div className="flex h-full min-h-[464px] flex-col overflow-hidden rounded-2xl bg-white shadow-md">
      <div className="relative flex grow items-center bg-primary-500 bg-cover bg-no-repeat p-8 text-white">
        <img src={beelineSvg} alt="Beeline" className="absolute right-10 top-20 z-[1] rotate-12 scale-150" />
        <div className="z-[2] w-full max-w-[640px]">{bannerComponent}</div>
      </div>
      {countdownComponent ? countdownComponent : null}
    </div>
  );
};

/**
 * Displays a contextual hero message based on the supplier's current participation state.
 * All amounts and counts are scoped to non-participating markets only.
 * Participation = offerConfig.isEnabled || !!offerSchedule
 *
 * Scenarios (evaluated in order):
 *   1. All participating           → "Congratulations! You have active offers for all of your available A/R." → No CTA.
 *   2. No invoices + offer options → "No invoices yet — place an offer now to be ready when they arrive."     → Build Offer CTA.
 *   3. No invoices, no options     → "There are no invoices available for early payment at this time."        → No CTA.
 *   4. Partial participation       → "You have {totalAmt} remaining for early payment."                       → Build Offer CTA. Tooltip showing invoice breakdown (content varies: included+excluded when excluded > 0, included only otherwise).
 *   5. No participation            → "You have {totalAmt} available for early payment."                       → Build Offer CTA. Tooltip showing invoice breakdown (content varies: included+excluded when excluded > 0, included only otherwise).
 *
 * Build Offer CTA is disabled for read-only users in all cases.
 */
const HeroBannerComponent = () => {
  const { t } = useTranslation();
  const readOnlyUser = useReadOnlyUser();
  const { asCurrency } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  // Filter out disabled markets
  const { data: takerMarkets } = useTakerMarkets((markets) => markets.filter((m) => m.marketIsEnabled));
  const buyers = useGroupByBuyer({ takerMarkets });
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();

  const allTakerMarkets = buyers.flatMap((b) => b.takerMarkets);
  const isParticipating = (m: (typeof allTakerMarkets)[number]) => m.offerConfig.isEnabled || !!m.offerSchedule;
  const isAllParticipating = allTakerMarkets.length > 0 && allTakerMarkets.every(isParticipating);
  const isPartiallyParticipating = !isAllParticipating && allTakerMarkets.some(isParticipating);
  const nonParticipatingMarkets = allTakerMarkets.filter((m) => !isParticipating(m));
  const convert = (amount: number, currency: string) =>
    convertCurrency({ amount, exchangeRates, from: currency, to: selectedCurrency });
  const { totalInvoiceCount, totalAmount, totalIncludedAmount, totalExcludedAmount, hasOfferOptions } =
    nonParticipatingMarkets.reduce(
      (acc, m) => ({
        totalInvoiceCount: acc.totalInvoiceCount + m.totalInvoiceCount,
        totalAmount: acc.totalAmount + convert(m.totalInvoiceAmount, m.currency),
        totalIncludedAmount: acc.totalIncludedAmount + convert(m.eligibleInvoiceAmount, m.currency),
        totalExcludedAmount: acc.totalExcludedAmount + convert(m.takerExcludedInvoiceAmount, m.currency),
        hasOfferOptions: acc.hasOfferOptions || m.offerOptions.newOffer.length > 0,
      }),
      { totalInvoiceCount: 0, totalAmount: 0, totalIncludedAmount: 0, totalExcludedAmount: 0, hasOfferOptions: false }
    );

  const titleKey = isAllParticipating
    ? 'homepage.titleAllParticipating'
    : totalInvoiceCount === 0 && hasOfferOptions
    ? 'homepage.titleNoInvoicesWithOfferOptions'
    : totalInvoiceCount === 0
    ? 'homepage.titleNoInvoices'
    : isPartiallyParticipating
    ? 'homepage.titlePartiallyParticipating'
    : 'homepage.title';

  const showCta = (totalInvoiceCount > 0 && !isAllParticipating) || (totalInvoiceCount === 0 && hasOfferOptions);
  const showTooltip = totalInvoiceCount > 0 && !isAllParticipating && totalAmount > 0;

  const handleBuildOfferClick = () => {
    trackEnterpriseEvent('build-offer-cta::clicked', {
      source: 'homepage-hero',
      totalAmount,
      customerCount: buyers.length,
      hasExcludedInvoices: totalExcludedAmount > 0,
    });
  };

  const now = new Date();
  const nextDeadline = takerMarkets
    .filter((market) => market.marketNextClearTime && new Date(market.marketNextClearTime) > now)
    .reduce(
      (earliest, takerMarket) => {
        if (!takerMarket.marketNextClearTime) {
          return earliest;
        }

        const marketTime = new Date(takerMarket.marketNextClearTime);

        if (!earliest || marketTime < earliest) {
          return marketTime;
        }

        return earliest;
      },
      null as Date | null
    );
  const countdownString = useCountdown(nextDeadline);

  return (
    <HeroBannerLayout
      bannerComponent={
        <>
          <div className="text-5xl leading-[120%]">
            <Trans
              i18nKey={titleKey}
              t={t}
              values={{
                totalAmount: asCurrency(totalAmount, selectedCurrency),
              }}
              components={{
                tooltip: showTooltip ? (
                  <Popover>
                    <Popover.Trigger className="ml-1 align-super">
                      <InfoCircleIcon className="size-4 fill-white" />
                    </Popover.Trigger>
                    <Popover.Content arrow collisionPadding={24} side="top">
                      <div className="max-w-72 space-y-2 text-center">
                        <div>
                          {totalExcludedAmount > 0
                            ? t('homepage.tooltipContent2', {
                                totalIncludedAmount: asCurrency(totalIncludedAmount, selectedCurrency),
                                totalExcludedAmount: asCurrency(totalExcludedAmount, selectedCurrency),
                              })
                            : t('homepage.tooltipContent1', {
                                totalIncludedAmount: asCurrency(totalIncludedAmount, selectedCurrency),
                              })}
                        </div>
                        {totalExcludedAmount > 0 && <div>{t('homepage.tooltipContent3')}</div>}
                      </div>
                    </Popover.Content>
                  </Popover>
                ) : (
                  <></>
                ),
              }}
            />
          </div>
          {showCta && (
            <div className="mt-6 w-fit">
              <ButtonLink
                dark
                disabled={readOnlyUser}
                size="lg"
                to={asEnterpriseLink('build-offer')}
                variant="cta"
                onClick={handleBuildOfferClick}
              >
                {t('core.buildOffer')}
              </ButtonLink>
            </div>
          )}
        </>
      }
      countdownComponent={
        <>
          {countdownString ? (
            <div className="m-4 flex items-center justify-center gap-2 rounded-md bg-gray-100 p-3">
              <ClockIcon className="shrink-0" />
              <div>
                <Trans
                  i18nKey="homepage.nextDeadline"
                  components={{ style: <span className="font-bold" /> }}
                  values={{ nextDeadline: countdownString }}
                />
              </div>
            </div>
          ) : null}
        </>
      }
    />
  );
};

const HeroBanner = () => (
  <QueryBoundaries
    LoadingComponent={() => (
      <HeroBannerLayout
        bannerComponent={
          <div className="space-y-4 pb-12">
            <div className="space-y-2">
              <Skeleton className="h-20" />
              <Skeleton className="h-20 w-3/4" />
            </div>
            <Skeleton className="h-20 w-60" />
          </div>
        }
      />
    )}
  >
    <HeroBannerComponent />
  </QueryBoundaries>
);

export default HeroBanner;
